import addDaysToDate from '@/helpers/addDaysToDate'
import getCompleteDateRange from '@/helpers/getCompleteDateRange'
import isDateInRange from '@/helpers/isDateInRange'
import scrollToTarget from '@/helpers/scrollToTarget'
import router from '@/router'
import { useReservationStore } from '@/stores/reservation'
import { DatePickerInstance } from '@vuepic/vue-datepicker'
import { useWindowSize } from '@vueuse/core'
import { cloneDeep, isArray } from 'lodash'
import { Ref, computed, onMounted, reactive, ref, watch } from 'vue'
import { useToast } from 'vue-toastification'

import useAuthUser from '@/composables/useAuthUser'
import useCountryCodes from '@/composables/useCountryCodes'
import useSearchBoxRetrieve from '@/composables/useSearchBoxRetrieve'
import useSearchBoxSuggest from '@/composables/useSearchBoxSuggest'
import { useSearchPlaces } from '@/composables/useSearchPlaces'

import { DestinationTypes } from '@/enums/destinationTypes'

import type { Suggestion } from '@/interfaces/mapboxSearchSuggestResponse'

import { getCompanyGroupPolicies } from '@/modules/groups/composables/useCompanyGroupPolicies'
import useAvailavilityValidation from '@/modules/hotels/composables/useAvailabilityValidation'
import { strictPolicyBreach } from '@/modules/hotels/helpers/strictPolicyBreach'
import { TransformedHotelPolicies } from '@/modules/hotels/interfaces/transformedHotelPolicies'
import useUserFavoriteHotels from '@/modules/users/composables/useUserFavoriteHotels'
import useUserSearch from '@/modules/users/composables/useUsersSearch'

import useHotels from '../composables/useHotels'
import { AvailabilityForm } from '../interfaces/availabilityForm'
import type { HotelSearch } from '../interfaces/hotelsSearch'
import { PoliciesFlexibility } from '../interfaces/policiesFlexibility'
import type { BranchSuggestion } from './useHotelBranches'
import useHotelGroupPolicies, {
	HOTEL_POLICY_SERVICE,
} from './useHotelGroupPolicies'
import useHotelSearchSettings from './useHotelSearchSettings'
import { usePlaceDetails } from './usePlaceDetails'
import { useSearchHotels } from './useSearchHotels'
import useValidationPreviousRequest from './useValidatePreviousRequest'

const useAvailabilityForm = (expandHotelForm: (value: boolean) => void) => {
	const reservationStore = useReservationStore()
	const { setReservation } = reservationStore

	const toast = useToast()
	const { validatePreviousRequestAsync } = useValidationPreviousRequest()

	const policyErrorsRef = ref<HTMLElement | null>()

	onMounted(() => {
		if (rating.value != 0) {
			fixRating.value = true
		}

		if (reservationStore.reservation) {
			availabilityForm.member = reservationStore.reservation.member
			availabilityForm.travelers = reservationStore.reservation.travelers
			availabilityForm.destination = reservationStore.reservation.destination
		}
	})

	const { user } = useAuthUser()
	const availabilityForm = reactive<AvailabilityForm>({
		member: undefined,
		travelers: [],
		destination: undefined,
		date: [],
		previousRequest: '',
	})

	const { availabilityValidation, raiseErrors } =
		useAvailavilityValidation(availabilityForm)

	const travelerFilters = ref<{ company?: number; group?: number }>({
		company: undefined,
		group: undefined,
	})

	const hotelFilters = ref<{ memberId?: number }>({
		memberId: user.value?.internal ? undefined : user.value?.id,
	})

	// Pre-selección del hotel al entrar desde el widget de favoritos.
	// Llega por query param y no por reservationStore porque AvailabilityPage
	// ejecuta resetReservation() en su setup, antes de que este composable monte.
	const favoriteHotelId = computed(() => {
		const raw = router.currentRoute.value.query.favoriteHotelId
		return raw ? Number(raw) : undefined
	})

	const favoriteHotelApplied = ref(false)

	const { data: favoriteHotels } = useUserFavoriteHotels({
		immediate: !!favoriteHotelId.value,
		params: {
			userId: computed(() => user.value?.id),
		},
	})

	watch(
		[favoriteHotels, favoriteHotelId],
		([hotels, hotelId]) => {
			if (favoriteHotelApplied.value || !hotelId) return

			const hotel = hotels?.data.find((item) => item.id === hotelId)
			if (!hotel) return

			favoriteHotelApplied.value = true

			// Forma de HotelSearch (no de lugar de Google): así handleSubmit lo
			// reconoce como hotel y usa lat/lng directas en vez de pedir place details.
			availabilityForm.destination = {
				id: hotel.id,
				name: hotel.name,
				amadeusCode: hotel.amadeusCode,
				rating: hotel.rating,
				isFavorite: true,
				isIndependent: hotel.isIndependent,
				latitude: hotel.latitude ? Number(hotel.latitude) : null,
				longitude: hotel.longitude ? Number(hotel.longitude) : null,
				stateName: hotel.stateName,
			} as unknown as Suggestion
		},
		{ immediate: true }
	)

	// Pre-selección del destino al entrar con «Buscar hotel» desde «Estadísticas de
	// hoteles» de Mi empresa. Llega por query param, igual que los favoritos, y con la
	// forma de lugar de Google con la que «Últimas búsquedas» repite un destino: el
	// `place_id` y su nombre. Fechas y viajeros los elige quien busca.
	const prefilledPlace = computed(() => {
		const { destinationPlaceId, destinationLabel } =
			router.currentRoute.value.query

		if (typeof destinationPlaceId !== 'string' || !destinationPlaceId)
			return undefined

		return {
			id: destinationPlaceId,
			name: typeof destinationLabel === 'string' ? destinationLabel : '',
		}
	})

	const prefilledPlaceApplied = ref(false)

	watch(
		prefilledPlace,
		(place) => {
			if (prefilledPlaceApplied.value || !place) return

			prefilledPlaceApplied.value = true
			availabilityForm.destination = place as unknown as Suggestion
		},
		{ immediate: true }
	)

	const {
		users: members,
		setQuery: setMemberQuery,
		searchUsers: searchMembers,
		isFetching: loadingMembers,
	} = useUserSearch({
		immediate: true,
		automaticSearch: false,
		params: {
			filters: {
				membersOnly: user.value?.internal ? 1 : undefined,
			},
		},
		userInternal: user.value?.internal,
	})

	const {
		data: users,
		setQuery: setUsersQuery,
		searchUsers,
		isFetching: loadingUsers,
		resetState,
	} = useUserSearch({
		resetCache: false,
		automaticSearch: false,
		params: {
			filters: travelerFilters,
		},
		userInternal: user.value?.internal,
	})

	const disclaimerIsOpen = ref(false)
	const showingPolicies = ref(false)
	const actualDisclaimer = ref('')
	const disclaimers = ref({
		guests: 'El número de viajeros no puede ser mayor a 6.',
		rooms: 'El número de habitaciones no puede ser mayor a 4.',
		ecr: 'La búsqueda de ECR es individual por viajero ',
	})
	const loading = ref(false)
	const fixRating = ref(false)
	const travelers = ref<number[]>([])
	const firstDatePicked = ref<Date>()
	const datePickerRef = ref<{ pickerRef: Ref<DatePickerInstance> } | null>(null)
	const {
		form,
		reservationNights,
		isFetching: searchingHotels,
		hotelVuelitate,
		policyErrorFlag,
		policyInformation,
		setFormValue,
		setFormValues,
		searchHotels,
		setSelectedUsers,
		hotels,
		errorMessage,
	} = useHotels()

	const selectedBranch = ref<BranchSuggestion>()
	const submittingSearch = ref(false)
	const {
		settings: searchSettings,
		isFetching: loadingSearchSettings,
		isError: searchSettingsError,
		refetch: reloadSearchSettings,
	} = useHotelSearchSettings(
		computed(() => availabilityForm.member?.id),
		computed(() => !!user.value?.internal),
		computed(() => availabilityForm.travelers.map((traveler) => traveler.id))
	)
	const customRadiusKm = ref<number>()
	const radiusInput = computed({
		get: () =>
			customRadiusKm.value ?? searchSettings.value?.defaultRadiusKm ?? 1,
		set: (value: number) => {
			customRadiusKm.value = value
		},
	})
	/**
	 * La empresa con la que BookingV2 arma los ajustes y las sedes
	 * (`CompanyHotelPreferences::travelerCompany`): la del primer viajero; sin viajeros, la
	 * del solicitante si busca un interno, o la de quien busca. Con `travelers[0]` a secas,
	 * elegir el primer viajero de la misma empresa contaba como cambio de empresa.
	 */
	const searchCompanyId = computed(
		() =>
			availabilityForm.travelers[0]?.companyId ??
			(user.value?.internal ?
				availabilityForm.member?.companyId
			:	user.value?.companyId)
	)
	watch([() => availabilityForm.member?.id, searchCompanyId], () => {
		selectedBranch.value = undefined
		customRadiusKm.value = undefined
		setFormValues({ companyBranchId: undefined, distance: undefined })
	})
	watch(
		() => availabilityForm.destination,
		(destination) => {
			if (destination) selectedBranch.value = undefined
		}
	)
	watch(
		() => form.value.companyBranchId,
		(id, previousId) => {
			if (!id && previousId && !selectedBranch.value?.isCompanyAddress)
				selectedBranch.value = undefined
		}
	)
	// La dirección de la empresa no lleva `companyBranchId`: si el punto del formulario
	// se mueve a otro (barra de resumen, mapa), ya no se busca ahí. Dos fuentes y no un
	// getter que arme un arreglo: así Vue compara cada valor, y un `setFormValues` que no
	// mueve el punto (al cambiar de alcance con los ajustes en caché) no la suelta.
	watch(
		[() => form.value.latitude, () => form.value.longitude],
		([latitude, longitude]) => {
			const branch = selectedBranch.value
			if (
				branch?.isCompanyAddress &&
				latitude !== undefined &&
				(Number(latitude) !== branch.latitude ||
					Number(longitude) !== branch.longitude)
			)
				selectedBranch.value = undefined
		}
	)
	watch(
		() => searchSettings.value?.allowCustomRadius,
		(allowed) => {
			if (!allowed) {
				customRadiusKm.value = undefined
				setFormValue('distance', undefined)
			}
		}
	)

	const rating = ref(form.value.Rating ?? 0)

	// const selectedGroup = computed(() => {
	//   return availabilityForm.travelers[0]?.companyGroup;
	// });

	const selectedGroupId = computed(() => {
		return availabilityForm.travelers[0]?.companyGroupId
	})

	// const selectedCompany = computed(() => {
	//   return availabilityForm.member?.company;
	// });

	const { policyObjectMap, groupPolicies, loadingPolicies, refetchPolicies } =
		useHotelGroupPolicies({
			companyGroupId: selectedGroupId,
		})

	const { width } = useWindowSize()

	const openDisclaimerModal = (disclaimer: string) => {
		actualDisclaimer.value = disclaimer
		disclaimerIsOpen.value = true
	}

	const removePerson = (id: number) => {
		const remainingPeople = availabilityForm.travelers.filter(
			(element) => element.id != id
		)

		availabilityForm.travelers = remainingPeople

		if (!errorMessage.value?.errors) return

		if (Object.keys(errorMessage.value.errors).length === 0) return
		Object.keys(errorMessage.value.errors).forEach((key) => {
			if (key !== 'users' && Array.isArray(errorMessage.value.errors[key])) {
				errorMessage.value.errors[key] = errorMessage.value.errors[key].filter(
					(error: any) => error.travelerId !== id
				)

				if (errorMessage.value.errors[key].length === 0) {
					delete errorMessage.value.errors[key]
				}
			}
		})
	}

	const resetGuestQuery = () => {
		setUsersQuery('')
	}

	const setHotelStars = (stars: number) => {
		fixRating.value = !fixRating.value

		if (fixRating.value) {
			rating.value = stars == 1 ? 2 : stars
		} else {
			rating.value = 0
		}

		setFormValue('Rating', rating.value)
	}

	const paintStars = (index: number) => {
		if (!fixRating.value) {
			rating.value = index + 1
		}
	}

	const closeModal = () => {
		disclaimerIsOpen.value = false
	}

	const onRemoveMember = () => {
		errorMessage.value = ''
		availabilityForm.member = undefined
		availabilityForm.travelers = []
		setMemberQuery('')
		searchMembers()
	}

	const onRemovePlace = () => {
		availabilityForm.destination = undefined
		resetSearchQueryHotels()
		restSearchResults()
		removeSelectedPlace()
		resetSuggestedPlacesByPoi()
	}

	const resetTravelers = () => (availabilityForm.travelers = [])

	const handleSubmit = async () => {
		if (submittingSearch.value || searchingHotels.value) return
		if (selectedBranch.value) {
			const pending = await searchAroundBranch(selectedBranch.value)
			if (pending.length)
				toast.info(
					`Para buscar cerca de ${selectedBranch.value.branchName}, elige ${pending.join(' y ')}.`
				)
			return
		}
		submittingSearch.value = true
		try {
			await reloadSearchSettings()
			setFormValue(
				'distance',
				searchSettings.value?.allowCustomRadius ?
					customRadiusKm.value
				:	undefined
			)
			if (availabilityForm.previousRequest) {
				const response = await validatePreviousRequestAsync({
					previousRequest: Number(availabilityForm.previousRequest),
				})

				setFormValue('previousRequestData', response?.data)
			} else {
				setFormValue('previousRequestData', undefined)
			}

			if (availabilityValidation.value.$invalid) {
				raiseErrors()
				return
			}

			const destination = availabilityForm.destination as
				| (Suggestion & Partial<HotelSearch>)
				| undefined

			const isHotelDestination =
				destination !== null &&
				destination !== undefined &&
				('isIndependent' in destination || 'isFavorite' in destination)

			if (isHotelDestination) {
				// Hotel destination: use lat/lng from the hotel record directly.
				// Do NOT call setSelectedPlaceById / retryDetails — destination.id is
				// a DB hotel id, not a Google place_id, and would cause a 400 error.
				setFormValues({
					companyBranchId: undefined,
					latitude: destination.latitude ?? undefined,
					longitude: destination.longitude ?? undefined,
					cityName: destination.stateName ?? destination.name,
					destinationCategory: DestinationTypes.poi,
					users: availabilityForm.travelers.map((element) => element.id),
					memberId:
						user.value?.internal ?
							availabilityForm.member?.id!
						:	user.value?.id,
					previousRequest: availabilityForm.previousRequest,
				})
			} else {
				// Place destination (city / region / airport / POI): id IS a Google
				// place_id — proceed with the standard Google place details flow.
				setSelectedPlaceById({
					placeId: destination?.id ?? '',
				})
				const resp = await retryDetails()

				if (resp) {
					setFormValues({
						longitude: resp.data?.location.longitude,
						latitude: resp.data?.location.latitude,
					})
				}

				setFormValues({
					companyBranchId: undefined,
					cityName: resp?.data?.displayName.text,
					destinationCategory: resp?.data?.types[0] as DestinationTypes,
					// Para el historial: con el place_id «Últimas búsquedas» vuelve a llenar
					// el formulario, y la etiqueta es lo que el usuario eligió.
					placeId: destination?.id,
					destinationLabel: destination?.name,
					users: availabilityForm.travelers.map((element) => element.id),
					memberId:
						user.value?.internal ?
							availabilityForm.member?.id!
						:	user.value?.id,
					previousRequest: availabilityForm.previousRequest,
				})
			}

			// Early return for manual reservation path:
			// offline (no id) or non-Amadeus hotel (has amadeusCode key but it's empty/null).
			// Place destinations have no amadeusCode key and always have an id, so they bypass this.
			const d = availabilityForm.destination
			const isHotel = d && ('isIndependent' in d || 'isFavorite' in d)
			const requiresManual = d && (!d.id || (isHotel && !d.amadeusCode))
			if (requiresManual) {
				return // manual reservation is triggered by the "Crear reserva manual" button → createReservation()
			}

			if (availabilityForm.destination?.amadeusCode) {
				const users = JSON.stringify(
					availabilityForm.travelers.map((element) => element.id)
				)

				const queryParams = {
					Start: form.value.Start,
					End: form.value.End,
					Quantity: form.value.Quantity,
					GuestCount: form.value.GuestCount,
					IsPerRoom: Number(form.value.IsPerRoom),
					HotelCode: availabilityForm.destination?.amadeusCode,
					users,
					memberId:
						user.value?.internal ?
							availabilityForm.member?.id!
						:	user.value?.id,
					hotelName: availabilityForm.destination?.name,
					previousRequest: availabilityForm.previousRequest,
					// Buscar por nombre de hotel no pasa por /hotels/search: el detalle es la
					// búsqueda, y esta marca le pide al backend registrarla en el historial.
					newSearch: 1,
				}

				const detailsUrl = await router.resolve({
					name: 'hotel.details',
					query: { ...queryParams },
				})
				window.open(detailsUrl.href, '_blank')

				reservationStore.reservation = undefined

				return
			}

			await searchHotels()

			if (errorMessage.value.errors) {
				expandHotelForm(true)
				return
			}
		} catch (err: any) {
			toast.error(
				err.response?.data?.message ?? 'No se pudo buscar disponibilidad.'
			)
		} finally {
			submittingSearch.value = false
		}
	}

	/**
	 * Buscar hoteles en el radio guardado de una sede. La sede no es un destino ni un
	 * hotel: no pasa por el campo «Destino» ni por Google. Se busca con su punto, su radio
	 * y su lista de preferidos (BookingV2 los pone con `companyBranchId`), y con los
	 * viajeros, fechas y huéspedes del formulario.
	 *
	 * Regresa lo que falta elegir; vacío = se buscó.
	 */
	const searchAroundBranch = async (
		branch: BranchSuggestion
	): Promise<string[]> => {
		if (submittingSearch.value || searchingHotels.value) return []
		selectedBranch.value = branch
		availabilityForm.destination = undefined
		const pending = [
			...(availabilityForm.travelers.length ? [] : ['a los viajeros']),
			...(user.value?.internal && !availabilityForm.member ?
				['al solicitante']
			:	[]),
			...(availabilityForm.date?.[0] && availabilityForm.date?.[1] ?
				[]
			:	['las fechas']),
		]

		if (pending.length) return pending

		submittingSearch.value = true
		try {
			if (availabilityForm.previousRequest) {
				const response = await validatePreviousRequestAsync({
					previousRequest: Number(availabilityForm.previousRequest),
				})

				setFormValue('previousRequestData', response?.data)
			} else {
				setFormValue('previousRequestData', undefined)
			}

			// Lo que hubiera en «Destino» no es lo que se busca ahora.
			availabilityForm.destination = undefined

			setFormValues({
				latitude: branch.latitude,
				longitude: branch.longitude,
				// La dirección de la empresa se busca como un punto, con la lista de la empresa,
				// y sin sede BookingV2 deja el radio personalizado si la agencia lo permite.
				companyBranchId: branch.branchId ?? undefined,
				distance:
					branch.isCompanyAddress && searchSettings.value?.allowCustomRadius ?
						customRadiusKm.value
					:	undefined,
				cityName: branch.branchName,
				destinationCategory: DestinationTypes.poi,
				placeId: undefined,
				destinationLabel: branch.branchName,
				users: availabilityForm.travelers.map((element) => element.id),
				memberId:
					user.value?.internal ? availabilityForm.member?.id! : user.value?.id,
				previousRequest: availabilityForm.previousRequest,
			})

			await searchHotels()

			if (errorMessage.value.errors) {
				expandHotelForm(true)
			}
		} catch (err: any) {
			toast.error(
				err.response?.data?.message ?? 'No se pudo buscar cerca de la sede.'
			)
		} finally {
			submittingSearch.value = false
		}

		return []
	}

	const createReservation = () => {
		if (availabilityValidation.value.$invalid) {
			raiseErrors()
			return
		}

		setReservation({
			destination: availabilityForm.destination,
			travelers: availabilityForm.travelers,
			member: availabilityForm?.member ?? user.value!,
			dates: availabilityForm.date,
			guestCount: form.value.GuestCount,
		})

		router.push({
			name:
				!user.value?.internal ?
					'hotel.reservation'
				:	'hotel.internal.reservation',
		})
	}

	const transformedSuggestions = computed(() => {
		return suggestedPlaces.value.map((place) => ({
			...place,
			name: `${place.name}, ${place.context.region?.name}, ${place.context.country?.name}`,
			id: place.mapboxId,
		}))
	})

	const availablePolicies = computed(() => {
		const keys = Object.keys(policyObjectMap.value)
		return (
			Boolean(keys.length) &&
			keys.every((key) => {
				return policyObjectMap.value[key as keyof TransformedHotelPolicies]
			})
		)
	})

	const policiesFlexibility = computed(() => {
		const policies = policyObjectMap.value

		return Object.keys(policies).reduce((acc, policyName) => {
			const policy = policies[policyName as keyof TransformedHotelPolicies]

			acc[policyName as keyof PoliciesFlexibility] =
				policy ? policy.isFlexible : undefined

			return acc
		}, {} as PoliciesFlexibility)
	})

	const dateRestrictions = computed(() => {
		const { daysInAdvanceRange } = policyObjectMap.value

		return {
			min:
				daysInAdvanceRange ?
					addDaysToDate(new Date(), Number(daysInAdvanceRange.rule[0].min)!)
				:	new Date(),
			max:
				daysInAdvanceRange ?
					addDaysToDate(new Date(), Number(daysInAdvanceRange.rule[0].max)!)
				:	undefined,
		}
	})

	const allowedRange = computed(() => {
		const { min, max } = dateRestrictions.value

		return max && getCompleteDateRange(min, max)
	})

	const calendarSettings = computed(() => {
		const { nightAmount } = policyObjectMap.value
		const policiesFlexbilility = policiesFlexibility.value

		return {
			minDate: firstDatePicked.value ?? new Date(),
			maxDate:
				(
					!policiesFlexbilility.nightAmount &&
					firstDatePicked.value &&
					nightAmount
				) ?
					addDaysToDate(
						new Date(firstDatePicked.value),
						Number(nightAmount?.rule[0]?.value)! - 1
					)
				:	undefined,
			maxRange:
				!policiesFlexbilility.nightAmount ?
					Number(policyObjectMap.value.nightAmount?.rule[0]?.value!) - 1
				:	undefined,
			allowedDates:
				!firstDatePicked.value && !policiesFlexbilility.daysInAdvanceRange ?
					allowedRange.value
				:	undefined,
		}
	})

	const handleDateUpdate = (modelData: Array<string>) => {
		const start = modelData ? modelData[0] : ''
		const end = modelData ? modelData[1] : ''

		setFormValue('Start', start)
		setFormValue('End', end)
	}

	const handleInternalDateChange = (date: Date | Date[]) => {
		if (
			!date ||
			(date &&
				!policiesFlexibility.value.daysInAdvanceRange &&
				allowedRange.value &&
				!isDateInRange((date as Date[])[0], allowedRange.value))
		) {
			return (firstDatePicked.value = undefined)
		}

		if (isArray(date) && date.length) {
			firstDatePicked.value = date[0]
		}
	}

	const handleInternalDateClear = () => {
		datePickerRef.value?.pickerRef?.clearValue()
	}

	/** Deja las fechas vacías, y el calendario otra vez con los días que permite la política. */
	const clearDates = () => {
		availabilityForm.date = []
		firstDatePicked.value = undefined
		setFormValues({ Start: '', End: '' })
	}

	/**
	 * Por qué la política rígida del grupo del primer viajero no deja buscar con estas
	 * fechas, o nada si sí se puede (`strictPolicyBreach`). Es la misma política que
	 * evalúa BookingV2 al buscar.
	 *
	 * Pide las políticas de ESE grupo por su id, aparte de las que el watch de viajeros
	 * carga para el calendario. Así no depende de cuándo llegan ésas al store ni de que
	 * otra petición cancele ésta, y nunca lee las del grupo anterior.
	 */
	const strictPolicyBlocking = async (
		checkIn: string,
		checkOut: string
	): Promise<string | undefined> => {
		const [first] = availabilityForm.travelers

		// Sin grupo no hay políticas: BookingV2 tampoco evalúa nada.
		if (!first?.companyGroupId) return undefined

		const policies = await getCompanyGroupPolicies({
			groupId: first.companyGroupId,
			filters: { service: HOTEL_POLICY_SERVICE },
		}).catch(() => null)

		// Si no se pudieron revisar, se busca: BookingV2 la evalúa igual y su error se pinta.
		if (!policies) return undefined

		const breach = strictPolicyBreach(policies, checkIn, checkOut)

		if (!breach) return undefined

		return first.groupName ?
				`${breach} (política del grupo «${first.groupName}»)`
			:	breach
	}

	const countryIds = computed(() => {
		const { allowedCountries } = policyObjectMap.value
		return allowedCountries?.rule[0].options ?? []
	})

	const {
		countryCodes,
		resetState: resetCountryCodes,
		refetch: refetchCountrycodes,
	} = useCountryCodes({
		params: {
			ids: countryIds,
		},
		immediate: false,
	})

	const commaSeparatedCountryCodes = computed(() => {
		return countryCodes.value.reduce((acc, country) => {
			acc += `${country.iso2},`
			return acc
		}, '')
	})

	const countryNames = computed(() => {
		return countryCodes.value.map((country) => country.name)
	})

	const countryCode = computed((): string[] => {
		return countryCodes.value.map((country) => country.iso2)
	})

	const transformedAllowedCountriesPolicy = computed(() => {
		const { allowedCountries } = cloneDeep(policyObjectMap.value)
		if (!allowedCountries) return

		allowedCountries.rule[0].options = countryNames.value as any

		return allowedCountries
	})

	const {
		setSearchQuery: setPlaceQuery,
		isFetching: loadingPlaces,
		suggestedPlaces,
		suggestedPoi,
		setSelectedPlace,
		removeSelectedPlace,
		suggestedPlacesByPoi,
		resetSuggestedPlacesByPoi,
	} = useSearchBoxSuggest({
		params: {
			country: commaSeparatedCountryCodes,
		},
	})

	const {
		error: searchPlaceError,
		isFetching: isFetchingSearchPlaces,
		searchPlaceQuery,
		searchPlaceResults,
		setSearchPlaceQuery,
		suggestionPlaces,
		setSelectedPlace: setSelectedPlaceById,
		suggestedFiltered,
		suggestedFilteredEstablishment,
	} = useSearchPlaces({
		params: {
			includedRegionCodes: countryCode,
			// 'geocode' no es un tipo de lugar válido en Places API (New) —
			// era una palabra reservada de la Autocomplete API vieja. Google
			// la ignora en silencio, pero de paso hacía perder una de las 5
			// entradas disponibles en includedPrimaryTypes sin aportar nada.
			includedPrimaryTypes: [
				'airport',
				'tourist_attraction',
				'locality',
				'establishment',
			],
		},
	})

	const {
		searchHotels: searchHotelsResults,
		error,
		isFetching,
		resultsHotelsSearch,
		resultsHotelsFavorites,
		searchQueryHotels,
		setSearchQueryHotels,
		resetSearchQueryHotels,
		restSearchResults,
	} = useSearchHotels({
		filters: hotelFilters,
	})

	const transformedPlacesResults = computed(() => {
		return suggestionPlaces?.value?.map((place) => ({
			name: place.placePrediction?.text?.text,
			id: place?.placePrediction.placeId,
		}))
	})

	const transformedResultsPlaces = computed(() => {
		return suggestedFiltered.value.map((placeFiltered) => ({
			name: placeFiltered.text?.text,
			id: placeFiltered.placeId,
		}))
	})

	const transformedResultsEstablishment = computed(() => {
		return suggestedFilteredEstablishment.value.map((placeFiltered) => ({
			name: placeFiltered.text?.text,
			id: placeFiltered.placeId,
		}))
	})

	const transformedSuggestionPois = computed(() => {
		return suggestedPoi.value?.map((place) => ({
			...place,
			name: `${place.name}, ${place.address}`,
			id: place.mapboxId,
		}))
	})

	const {
		retrievePlace,
		retrievedPlace,
		retrievePlaceCoordinates,
		isFetching: retrievingPlace,
	} = useSearchBoxRetrieve()

	const {
		placeSelected: placeSelcet,
		isFetching: isLoadingPlace,
		refetch,
		retryDetails,
		placeDetails,
	} = usePlaceDetails()

	watch(groupPolicies, async (value) => {
		if (value.length) await refetchCountrycodes()
	})

	watch(
		() => availabilityForm.travelers,
		async (currentValue) => {
			travelerFilters.value.group =
				currentValue.length ? currentValue[0].companyGroupId : undefined

			travelers.value = currentValue.map((element) => {
				return element.id
			})

			if (!currentValue.length && availabilityForm.member) {
				handleInternalDateClear()
				availabilityForm.destination = undefined
				resetCountryCodes
				resetState()
			}

			if (currentValue.length == 1) {
				await refetchPolicies()
				await refetchCountrycodes()
			}

			setFormValue('Quantity', currentValue.length)
			setSelectedUsers(travelers.value)
			resetGuestQuery()
		},
		{
			deep: true,
		}
	)

	watch(
		() => availabilityForm.member,
		(currentValue) => {
			if (currentValue) {
				hotelFilters.value.memberId = currentValue.id

				travelerFilters.value.company = currentValue.companyId
				resetState()
				searchUsers()
			} else {
				handleInternalDateClear()
				availabilityForm.destination = undefined
				travelerFilters.value = {
					company: undefined,
					group: undefined,
				}
			}
		}
	)

	watch(searchingHotels, (value) => {
		if (value && form.value.Rating === 0) {
			fixRating.value = false
			rating.value = 0
		}
	})

	watch(policyErrorFlag, (value) => {
		if (value && policyInformation.value?.errors?.length) {
			expandHotelForm(true)

			setTimeout(() => {
				if (!policyErrorsRef.value) return
				scrollToTarget(policyErrorsRef.value)
			}, 500)
		}
	})

	return {
		availabilityForm,
		selectedBranch,
		searchSettings,
		searchCompanyId,
		loadingSearchSettings,
		searchSettingsError,
		reloadSearchSettings,
		radiusInput,
		submittingSearch,
		availabilityValidation,
		user,
		members,
		loadingMembers,
		users,
		loadingUsers,
		disclaimerIsOpen,
		actualDisclaimer,
		disclaimers,
		loading,
		fixRating,
		travelers,
		form,
		reservationNights,
		searchingHotels,
		hotelVuelitate,
		rating,
		loadingPlaces,
		transformedSuggestions,
		transformedSuggestionPois,
		retrievingPlace,
		retrievedPlace,
		retrievePlaceCoordinates,
		width,
		travelerFilters,
		// selectedGroup,
		// selectedCompany,
		policyErrorFlag,
		policyInformation,
		policyErrorsRef,
		groupPolicies,
		policyObjectMap,
		dateRestrictions,
		firstDatePicked,
		allowedRange,
		datePickerRef,
		loadingPolicies,
		countryIds,
		countryCodes,
		policiesFlexibility,
		calendarSettings,
		showingPolicies,
		availablePolicies,
		countryNames,
		transformedAllowedCountriesPolicy,
		handleInternalDateChange,
		clearDates,
		strictPolicyBlocking,
		raiseErrors,
		setUsersQuery,
		searchUsers,
		setFormValue,
		setFormValues,
		searchHotels,
		setSelectedUsers,
		setPlaceQuery,
		setMemberQuery,
		searchMembers,
		setSelectedPlace,
		removeSelectedPlace,
		retrievePlace,
		openDisclaimerModal,
		removePerson,
		resetGuestQuery,
		handleDateUpdate,
		setHotelStars,
		paintStars,
		closeModal,
		onRemoveMember,
		onRemovePlace,
		handleSubmit,
		searchAroundBranch,
		resetState,
		resetTravelers,

		// Hotel search
		searchHotelsResults,
		error,
		isFetching,
		resultsHotelsSearch,
		searchQueryHotels,
		setSearchQueryHotels,
		resultsHotelsFavorites,

		// Google places search
		searchPlaceQuery,
		searchPlaceResults,
		setSearchPlaceQuery,
		isFetchingSearchPlaces,
		transformedPlacesResults,
		transformedResultsPlaces,
		transformedResultsEstablishment,

		//Place Details
		isLoadingPlace,

		// Reservation
		createReservation,
		errorMessage,
	}
}

export default useAvailabilityForm
